import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IssuedCommand } from '../services/simulationApi';

// Live simulation events from the /drone-operator socket (SCRUM-1830).
// Compact by design — full decision records (stateSnapshot, llmOutput) come
// from GET /simulations/:id/decisions.

export interface SimulationOwnershipEvent {
  simulationId: string;
  userId: string | null;
  previousUserId: string | null;
  acquiredAt: string | null;
}

export interface SimulationDecisionEvent {
  decisionId: string;
  simulationId: string;
  teamId: string;
  tickNumber: number;
  commandsIssued: IssuedCommand[];
  latencyMs: number;
  model: string;
  createdAt: string;
}

const MAX_DECISIONS_PER_SIMULATION = 100;

export interface SimulationLiveState {
  // Latest ownership broadcast per simulation; REST detail is the initial source.
  ownership: Record<string, SimulationOwnershipEvent>;
  // Newest-first ring buffer of decision events per simulation.
  decisions: Record<string, SimulationDecisionEvent[]>;
}

const initialState: SimulationLiveState = {
  ownership: {},
  decisions: {},
};

const simulationLiveSlice = createSlice({
  name: 'simulationLive',
  initialState,
  reducers: {
    simulationOwnershipChanged: (state, action: PayloadAction<SimulationOwnershipEvent>) => {
      state.ownership[action.payload.simulationId] = action.payload;
    },

    simulationDecisionReceived: (state, action: PayloadAction<SimulationDecisionEvent>) => {
      const { simulationId, decisionId } = action.payload;
      const list = state.decisions[simulationId] ?? [];
      if (list.some((d) => d.decisionId === decisionId)) return;
      state.decisions[simulationId] = [action.payload, ...list].slice(
        0,
        MAX_DECISIONS_PER_SIMULATION,
      );
    },

    clearSimulationDecisions: (state, action: PayloadAction<string>) => {
      delete state.decisions[action.payload];
    },
  },
});

export const { simulationOwnershipChanged, simulationDecisionReceived, clearSimulationDecisions } =
  simulationLiveSlice.actions;

export default simulationLiveSlice.reducer;
